import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Shield, Database, Settings, CheckCircle, Mail } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { colors } from '../../config/colors';
import { getEducationalDisclaimerSections } from '../../api';

const iconMap = { Shield, Database, Settings, CheckCircle, Mail };

const HeaderSection = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 50%, ${colors.secondary[500]} 100%)`,
  padding: theme.spacing(8, 2),
  [theme.breakpoints.up('4xl')]: {
    padding: theme.spacing(16, 2),
  },
  borderRadius: '0 0 40px 40px',
  marginBottom: theme.spacing(4),
  color: 'white',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3,
  },
}));

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  border: `2px solid ${colors.gray[200]}`,
  overflow: 'hidden',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    borderColor: colors.primary[400],
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: 'transparent',
  color: colors.primary[700],
  border: 'none',
  borderRadius: '12px',
  padding: theme.spacing(1, 2),
  minHeight: 'auto',
  '&:hover': {
    backgroundColor: colors.primary[50],
    color: colors.primary[600],
  },
}));

const EducationalDisclaimer = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [sections, setSections] = useState([]);

  const fetchSections = async () => {
    try {
      const stored = await getEducationalDisclaimerSections();
      const list = Array.isArray(stored) ? stored : [];
      setSections(list);
      if (list.length > 0) {
        setActiveSection(list[0]?.id ?? 'general');
      }
    } catch {
      setSections([]);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nodes = sections.map((s) => document.getElementById(s.id));
      const scrollPos = window.scrollY + 200;

      for (let i = nodes.length - 1; i >= 0; i--) {
        if (nodes[i] && nodes[i].offsetTop <= scrollPos) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />

      <main className="flex-grow">
        <HeaderSection elevation={0}>
          <Container maxWidth={false} sx={{ maxWidth: { lg: 1200, xl: 1400, '2xl': 1600, '3xl': 1800, '4xl': 2200 } }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3.5rem', '4xl': '5rem' },
                position: 'relative',
                zIndex: 1,
              }}
            >
              Educational Disclaimer
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem', '4xl': '2rem' },
                position: 'relative',
                zIndex: 1,
                maxWidth: { xs: 700, '4xl': 1200 },
                mx: 'auto',
              }}
            >
              Our content is for educational purposes only. Nothing on this platform constitutes investment or financial advice.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, position: 'relative', zIndex: 1, fontSize: { '4xl': '1.25rem' } }}>
              Last updated: February {new Date().getFullYear()}
            </Typography>
          </Container>
        </HeaderSection>

        <Container maxWidth={false} sx={{ py: 4, pb: 8, maxWidth: { lg: 1200, xl: 1400, '2xl': 1600, '3xl': 1800, '4xl': 2200 } }}>
          {sections.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '20px',
                backgroundColor: 'white',
                border: `2px solid ${colors.gray[200]}`,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Data is not added
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 4, '4xl': 8 },
                alignItems: 'flex-start',
              }}
            >
            <Box
              sx={{
                display: { xs: 'none', sm: 'block' },
                width: { xs: '100%', sm: 280, '4xl': 400 },
                flexShrink: 0,
                position: { sm: 'sticky' },
                top: { sm: 100, '4xl': 120 },
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  border: `2px solid ${colors.gray[200]}`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 'bold',
                    color: colors.primary[600],
                    letterSpacing: 1.5,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  On this page
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {sections.map((section) => {
                    const IconComponent = iconMap[section.iconName];
                    return (
                      <NavButton
                        key={section.id}
                        fullWidth
                        startIcon={IconComponent && <IconComponent size={18} color={colors.primary[500]} />}
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          fontWeight: activeSection === section.id ? 'bold' : 500,
                          backgroundColor: activeSection === section.id ? colors.primary[100] : 'transparent',
                        }}
                      >
                        {section.title}
                      </NavButton>
                    );
                  })}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, '4xl': 6 } }}>
                {sections.map((section, index) => {
                  const IconComponent = iconMap[section.iconName];
                  const useSecondary = index % 2 === 1;
                  const isContact = section.isContact;

                  if (isContact) {
                    return (
                      <Paper
                        key={section.id}
                        id={section.id}
                        sx={{
                          p: { xs: 4, '4xl': 8 },
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${colors.primary[50]}, ${colors.secondary[50]})`,
                          border: `2px solid ${colors.primary[200]}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {IconComponent && <IconComponent size={24} color="white" />}
                          </Box>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[700], fontSize: { '4xl': '2.5rem' } }}>
                            {section.title}
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line', fontSize: { '4xl': '1.5rem' } }}>
                          {(section.content || '').replace(/\s*\|\s*/g, '\n')}
                        </Typography>
                      </Paper>
                    );
                  }

                  return (
                    <StyledCard key={section.id} id={section.id} sx={{ p: { xs: 4, '4xl': 8 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: `linear-gradient(135deg, ${useSecondary ? colors.secondary[100] : colors.primary[100]}, ${useSecondary ? colors.secondary[200] : colors.primary[200]})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {IconComponent && <IconComponent size={24} color={useSecondary ? colors.secondary[600] : colors.primary[600]} />}
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[700], fontSize: { '4xl': '2.5rem' } }}>
                          {section.title}
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: { '4xl': '1.5rem' } }}>
                        {section.content || ''}
                      </Typography>
                    </StyledCard>
                  );
                })}
              </Box>
            </Box>
            </Box>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default EducationalDisclaimer;
